const { registerBlockType } = wp.blocks;
const { InspectorControls, PlainText, RichText } = wp.blockEditor;
const { PanelBody, TextControl, SelectControl, TextareaControl } =
  wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

const ensureEmbedInline = (url) => {
  if (!url.includes('?embed=inline')) {
    url = url.includes('?') ? `${url}&embed=inline` : `${url}?embed=inline`;
  }

  url = url.replace('/share/', '/demo/');

  return url;
};

registerBlockType('pg/storylane-embed', {
  title: 'Storylane Embed',
  icon: 'embed-generic',
  category: 'embed',
  attributes: {
    title: {
      type: 'string',
      default: '',
    },
    description: {
      type: 'string',
      default: '',
    },
    iframeUrl: {
      type: 'string',
      default: '',
    },
    sectionClass: {
      type: 'string',
      default: 'section-light-primary',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-light-primary',
    },
    mode: {
      type: 'string',
      default: 'embed',
    },
    overlayCode: {
      type: 'string',
      default: '',
    },
  },
  edit: ({ attributes, setAttributes }) => {
    const {
      iframeUrl,
      sectionClass,
      cornerPositionClass,
      cornerBackgroundClass,
      description,
      title,
      mode,
      overlayCode,
    } = attributes;

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Display Mode')}>
            <SelectControl
              label={__('Mode')}
              value={attributes.mode}
              options={[
                { label: 'Embed', value: 'embed' },
                { label: 'Overlay', value: 'overlay' },
              ]}
              onChange={(value) => setAttributes({ mode: value })}
            />
            {attributes.mode === 'overlay' && (
              <TextareaControl
                label={__('Overlay HTML Code')}
                help={__(
                  'Paste the full HTML block from Storylane overlay option.'
                )}
                value={attributes.overlayCode}
                onChange={(value) => setAttributes({ overlayCode: value })}
                rows={8}
              />
            )}

            {attributes.mode === 'embed' && (
              <TextControl
                label={__('Iframe URL')}
                value={iframeUrl}
                onChange={(value) => setAttributes({ iframeUrl: value })}
                placeholder="https://app.storylane.io/demo/your-demo-id?embed=inline"
              />
            )}
          </PanelBody>

          <PanelBody title={__('Section Settings')}>
            <SelectControl
              label={__('Section Class')}
              value={sectionClass}
              options={[
                { label: 'Light Primary', value: 'section-light-primary' },
                { label: 'Light Secondary', value: 'section-light-secondary' },
                { label: 'Dark Primary', value: 'section-dark-primary' },
                { label: 'Dark Secondary', value: 'section-dark-secondary' },
              ]}
              onChange={(value) => setAttributes({ sectionClass: value })}
            />
            <SelectControl
              label={__('Corner Position')}
              value={cornerPositionClass}
              options={[
                { label: 'None', value: 'corner-disabled' },
                { label: 'Left-Top', value: 'corner-left-top' },
                { label: 'Left-Bottom', value: 'corner-left-bottom' },
                { label: 'Right-Top', value: 'corner-right-top' },
                { label: 'Right-Bottom', value: 'corner-right-bottom' },
              ]}
              onChange={(value) =>
                setAttributes({ cornerPositionClass: value })
              }
            />
            <SelectControl
              label={__('Corner Background')}
              value={cornerBackgroundClass}
              options={[
                { label: 'Light Primary', value: 'corner-on-light-primary' },
                {
                  label: 'Light Secondary',
                  value: 'corner-on-light-secondary',
                },
                { label: 'Dark Primary', value: 'corner-on-dark-primary' },
                { label: 'Dark Secondary', value: 'corner-on-dark-secondary' },
              ]}
              onChange={(value) =>
                setAttributes({ cornerBackgroundClass: value })
              }
            />
          </PanelBody>
        </InspectorControls>
        <section className={sectionClass}>
          <div
            className={`corner ${cornerPositionClass} ${sectionClass.replace(
              'section',
              'corner'
            )} ${cornerBackgroundClass}`}
          ></div>
          <div className="section-wrapper storylane-embed-block">
            <div className="content-container">
              <div className="content-wrapper">
                <RichText
                  tagName="h2"
                  value={title}
                  onChange={(value) => setAttributes({ title: value })}
                  placeholder={__('Title...')}
                />
                <div className={'description'}>
                  <RichText
                    value={description}
                    onChange={(newDescription) =>
                      setAttributes({ description: newDescription })
                    }
                    placeholder={__('Description')}
                  />
                </div>
                {attributes.mode === 'embed' && iframeUrl ? (
                  <div className={'storylane-embed-block-container'}>
                    <iframe
                      loading="lazy"
                      className="storylane-embed-block-container-sl"
                      src={ensureEmbedInline(iframeUrl)}
                      name="sl-embed"
                      allow="fullscreen"
                      allowFullScreen
                    />
                  </div>
                ) : attributes.mode === 'overlay' && attributes.overlayCode ? (
                  <div
                    className="storylane-embed-overlay"
                    dangerouslySetInnerHTML={{ __html: attributes.overlayCode }}
                  />
                ) : (
                  <p>
                    {__('Provide an iframe URL or overlay HTML code.', 'pg')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    const {
      iframeUrl,
      sectionClass,
      cornerPositionClass,
      cornerBackgroundClass,
      title,
      description,
      mode,
      overlayCode,
    } = attributes;

    return (
      <section className={sectionClass}>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="section-wrapper storylane-embed-block">
          <div className="content-container">
            <div className="content-wrapper">
              {title && <RichText.Content tagName="h2" value={title} />}
              {description && (
                <div
                  className={'description'}
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                ></div>
              )}
              {mode === 'embed' && iframeUrl ? (
                <>
                  <script
                    async
                    src="https://js.storylane.io/js/v2/storylane.js"
                  ></script>
                  <div className={'storylane-embed-block-container'}>
                    <iframe
                      loading="lazy"
                      className="storylane-embed-block-container-sl"
                      src={ensureEmbedInline(iframeUrl)}
                      name="sl-embed"
                      allow="fullscreen"
                      allowFullScreen
                    />
                  </div>
                </>
              ) : mode === 'overlay' && overlayCode ? (
                <div
                  className="storylane-embed-overlay"
                  dangerouslySetInnerHTML={{ __html: overlayCode }}
                />
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  },
});
